import { createVeriffFrame, MESSAGES } from '@veriff/incontext-sdk'

import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useState,
} from 'react'

import { useNavigate } from 'react-router-dom'
import { IdCard, CheckCircle } from 'lucide-react'

const PreCheck = forwardRef(({
    data,
    onSubmitted,
    documentText,
}, ref) => {

    useImperativeHandle(ref, () => ({
        validateAndGetData: () => ({
            isValid: true,
            value: data,
        }),
    }))

    const navigate = useNavigate()

    const handleRedirect = useCallback(() => {
        if (onSubmitted) {
            onSubmitted()
        } else {
            navigate('/pre-check-form-success')
        }
    }, [onSubmitted, navigate])

    useEffect(() => {
        if (!data?.sessionUrl) {
            return
        }
        if (data?.status === "submitted") {
            return
        }
        createVeriffFrame({
            url: data.sessionUrl,
            embedded: true,
            embeddedOptions: {
                rootElementID: 'embedded-container',
            },
            onEvent: function (msg) {
                switch (msg) {
                    case MESSAGES.STARTED:
                        console.log('Verification started');
                        break;
                    case MESSAGES.SUBMITTED: {
                        handleRedirect()
                        break;
                    }
                    case MESSAGES.FINISHED:
                        console.log('Verification finished');
                        break;
                    case MESSAGES.CANCELED:
                        console.log('Verification closed');
                        break;
                    case MESSAGES.RELOAD_REQUEST:
                        console.log('Verification reloaded');
                        break;
                }
            },
        })

    }, [data?.sessionUrl, data?.status])

    const isAlreadySubmitted = data?.status === 'submitted'

    return (
        <div className="flex items-center justify-center px-4 py-10">
            <div className="max-w-3xl w-full bg-white shadow-xl rounded-2xl p-8 space-y-6">
                <div className="space-y-2">
                    <h1 className="text-2xl font-bold text-gray-800">Identity Verification</h1>
                    {documentText && (
                        <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 text-blue-800 text-sm font-medium px-3 py-1.5 rounded-lg">
                            <IdCard className="w-4 h-4 shrink-0" />
                            {documentText}
                        </div>
                    )}
                    <p className="text-gray-600 text-base">
                        Please complete the identity verification steps below.
                    </p>
                </div>

                {isAlreadySubmitted ? (
                    <div className="flex flex-col items-center justify-center py-16 gap-5 text-center">
                        <CheckCircle className="w-16 h-16 text-green-500" />
                        <div className="space-y-1">
                            <h2 className="text-xl font-semibold text-gray-800">Verification Already Submitted</h2>
                            <p className="text-gray-500 text-sm max-w-sm">
                                Your identity verification has already been submitted.
                            </p>
                            <div className="text-center">
                                <button
                                    onClick={handleRedirect}
                                    className="px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-lg shadow-md transition-all disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                                >
                                    Continue
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="w-full md:w-[635px] h-auto md:h-[670px] min-h-[670px]" id="embedded-container" />

                        {!data?.sessionUrl && (
                            <p className="text-sm text-red-600">
                                Session does not exist or has expired. Please contact support.
                            </p>
                        )}
                    </>
                )}
            </div>
        </div>
    )

})

export default PreCheck
